import type { CampoUi, EsquemaRotina, TipoCampoUi } from './tipos';

/** Tipos que o servidor pode mandar e o componente de campo de cada um (07 §3.3). */
export const COMPONENTE_POR_TIPO: Record<TipoCampoUi, 'CampoTexto' | 'CampoNumero' | 'CampoData' | 'Interruptor' | 'Selecao' | 'CampoConsulta' | 'AreaTexto' | 'CampoArquivo' | 'CampoCor'> = {
  texto: 'CampoTexto', cpfCnpj: 'CampoTexto', cep: 'CampoTexto', telefone: 'CampoTexto', email: 'CampoTexto', senha: 'CampoTexto',
  numero: 'CampoNumero', moeda: 'CampoNumero', quantidade: 'CampoNumero', preco: 'CampoNumero', percentual: 'CampoNumero',
  data: 'CampoData', dataHora: 'CampoData', hora: 'CampoData',
  logico: 'Interruptor', lista: 'Selecao', consulta: 'CampoConsulta', memo: 'AreaTexto', arquivo: 'CampoArquivo', imagem: 'CampoArquivo', cor: 'CampoCor',
};

const TIPOS = new Set(Object.keys(COMPONENTE_POR_TIPO));
const LARGURAS = [1, 2, 3, 4, 6, 12] as const;

/** Máscaras fixas por tipo quando o dicionário não define (07 §3.3). */
const MASCARA_PADRAO: Partial<Record<TipoCampoUi, string>> = { cep: '99999-999' };

/** '@R 99.999-999' (formato herdado do dicionário) → '99.999-999'; '@!' (só maiúsculas) não é máscara de posição. */
export function converterMascara(m: string | null | undefined): string | undefined {
  if (!m) return undefined;
  const t = m.replace(/^@[A-Z!]+\s*/, '').trim();
  return t.length > 0 ? t : undefined;
}

function campo(bruto: Record<string, unknown>): CampoUi {
  const tipo = String(bruto.tipo);
  if (!TIPOS.has(tipo)) throw new Error(`Tipo de campo desconhecido no esquema: ${tipo}`);
  const largura = Number(bruto.largura);
  const t = tipo as TipoCampoUi;
  return {
    ...(bruto as unknown as CampoUi),
    tipo: t,
    mascara: converterMascara(bruto.mascara as string | undefined) ?? MASCARA_PADRAO[t],
    largura: (LARGURAS as readonly number[]).includes(largura) ? (largura as CampoUi['largura']) : 3,
    modo: (bruto.modo as CampoUi['modo']) ?? { inclusao: !bruto.somenteLeitura, alteracao: !bruto.somenteLeitura },
    mono: Boolean(bruto.mono),
  };
}

/** Converte o esquema do servidor (01 §6.2) na forma que as páginas usam; nenhum componente lê o bruto. */
export function adaptarEsquema(bruto: unknown): EsquemaRotina {
  const e = bruto as Record<string, unknown>;
  const campos = ((e.campos as Record<string, unknown>[]) ?? []).map(campo).sort((a, b) => a.ordem - b.ordem);
  const itens = e.itens as { alias: string; titulo: string; campos: Record<string, unknown>[]; totais: string[] } | undefined;
  return {
    ...(e as unknown as EsquemaRotina),
    campos,
    itens: itens ? { ...itens, campos: itens.campos.map(campo) } : undefined,
    detalhes: ((e.detalhes as { alias: string; titulo: string; campos: Record<string, unknown>[]; totais: string[] }[] | undefined) ?? [])
      .map((d) => ({ ...d, campos: d.campos.map(campo).sort((a, b) => a.ordem - b.ordem) })),
    pastas: ((e.pastas as EsquemaRotina['pastas']) ?? []).slice().sort((a, b) => a.ordem - b.ordem),
    acoes: (e.acoes as EsquemaRotina['acoes']) ?? [],
  };
}

export const camposLista = (e: EsquemaRotina) => e.campos.filter((c) => c.visivelEm.lista);
export const camposFormulario = (e: EsquemaRotina) => e.campos.filter((c) => c.visivelEm.formulario);
export const camposDetalhe = (e: EsquemaRotina) => e.campos.filter((c) => c.visivelEm.detalhe);
export const podeAcao = (e: EsquemaRotina, codigo: string) => e.acoes.some((a) => a.codigo === codigo);

/** Colunas que indicam o registro no título ("000123 · ACME LTDA"): chave + primeiro texto não-chave. */
export function tituloRegistro(e: EsquemaRotina, r: Record<string, unknown>): string {
  const chave = e.campos.find((c) => c.chave && c.visivelEm.lista);
  const descricao = e.campos.find((c) => !c.chave && c.tipo === 'texto' && c.visivelEm.lista);
  return [chave && r[chave.nome], descricao && r[descricao.nome]].filter((v) => v !== undefined && v !== null && v !== '').join(' · ') || `#${r.id}`;
}
